import React, { Suspense, useEffect } from 'react';
import { isEmpty } from 'lodash';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { PAGES } from '../../utils/pages';
import { ROUTES } from '../../utils/routes';
import { Layout } from './styled/Layout';
import { PageWrapper } from './styled/PageWrapper';
import { Header } from '../Header';
import { SimpleSnackbar } from '../common/Snackbar/snackbar';
import { APP_INIT_STATUS, USER_ROLES } from '../../utils/constants';
import { ErrorImage } from '../common/Card/CardError/ErrorImage';
import { BlockNavigationModal } from '../common/BlockNavigationModal';
import { useCacheBuster } from '../common/CacheBuster/useCacheBuster';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { EmptyTeamList } from '../common/EmptyTeamList';

import { getAppConfig } from '../../store/selectors/appConfig';
import { getUserRole } from '../../store/selectors/user';
import { selectTeamsAsArray } from '../../store/selectors/entities';
import { logout } from '../../store/actions/user';

import { useLocalizationContext } from '../../localization/LocalizationContext';
import { useAppDispatch, useAppSelector } from '../../store/hooks';

const getLoader = () => {
    return (
        <Box minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
            <CircularProgress size={60} />
        </Box>
    );
};

const Page = (props: { page: string }) => {
    const route = ROUTES[props.page];

    const PageComponent = route?.component;

    if (!PageComponent) return null;

    return <PageComponent />;
};

const CheckTeams: React.FC<{ children: React.ReactElement }> = ({ children }) => {
    const localization = useLocalizationContext();
    const teamsList = useAppSelector(selectTeamsAsArray);

    if (isEmpty(teamsList)) {
        const message = localization.getText('common.emptyTeamsMessage');
        return (
            <Layout>
                <Header />
                <PageWrapper>
                    <Suspense fallback={getLoader()}>
                        <EmptyTeamList message={message} />
                    </Suspense>
                </PageWrapper>
            </Layout>
        );
    }

    return children;
};

const FEATURE_FLAGS = 'featureFlags';

export const App: React.FC = () => {
    const localization = useLocalizationContext();

    const dispatch = useAppDispatch();

    const page = useAppSelector(state => state.page ?? '');
    const appStatus = useAppSelector(state => state.common.appStatus) as string;
    const appConfig = useAppSelector(state => getAppConfig(state));
    const userRole = useAppSelector(getUserRole);

    const { loading, isLatestVersion, refreshCacheAndReload } = useCacheBuster();
    const [, setLocalStorage] = useLocalStorage<Record<string, boolean | string | number>>(FEATURE_FLAGS, appConfig);

    useEffect(() => {
        setLocalStorage(appConfig);
    }, [setLocalStorage, appConfig]);

    useEffect(() => {
        if (!loading && !isLatestVersion) {
            refreshCacheAndReload();
        }
    }, [loading, isLatestVersion, refreshCacheAndReload]);

    if (loading) return getLoader();

    if (page === PAGES.LOGIN) {
        return (
            <Layout>
                <Page page={page} />
            </Layout>
        );
    }

    if (page === PAGES.RECOVERY) {
        return (
            <Layout>
                <Suspense fallback={getLoader()}>
                    <Page page={page} />
                </Suspense>
            </Layout>
        );
    }

    const renderLayout = () => (
        <Layout>
            <Header />
            <PageWrapper>
                <Suspense fallback={getLoader()}>
                    <Page page={page} />
                </Suspense>
                <SimpleSnackbar />
            </PageWrapper>
            <BlockNavigationModal />
        </Layout>
    );

    switch (appStatus) {
        case APP_INIT_STATUS.INITIAL:
        case APP_INIT_STATUS.PROGRESS:
            return getLoader();
        case APP_INIT_STATUS.SUCCESS:
            if (userRole === USER_ROLES.CORE_TEAM_LEAD) {
                return <CheckTeams>{renderLayout()}</CheckTeams>;
            }
            return renderLayout();
        case APP_INIT_STATUS.FAIL:
        default:
            return (
                <Box
                    minHeight="100vh"
                    display="flex"
                    alignItems="center"
                    flexDirection="column"
                    justifyContent="center"
                >
                    <ErrorImage />
                    <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
                        <Typography variant="h4">
                            {localization.getText('common.appInitializationFailed.title')}
                        </Typography>
                        <Typography align="center" variant="body2">
                            {localization.getText('common.appInitializationFailed.subTitle')}
                        </Typography>
                        <Button
                            sx={{ marginTop: 20 }}
                            onClick={() => {
                                dispatch(logout());
                            }}
                            name="login-submit"
                            type="submit"
                            variant="contained"
                            color="primary"
                            data-test="login-submit"
                        >
                            {localization.getText('common.appInitializationFailed.button')}
                        </Button>
                    </Box>
                </Box>
            );
    }
};
